import { useMemo } from 'react';
import './LivePreview.css';

// Playful emojis that appear based on playfulness level
const PLAYFUL_EMOJIS = ['✨', '🎉', '💫', '🌟', '🎨', '💖', '🦋', '🌈', '🎪', '🎭'];

export default function LivePreview({ formData, styles }) {
  const { brandCore, visuals, scales } = formData;
  
  // Calculate how playful the design is (0-100, inverted because low = playful)
  const playfulness = 100 - scales.playfulSerious;
  const experimentalness = scales.safeExperimental;
  
  // Generate floating emojis based on playfulness (only when > 60, i.e., slider < 40)
  const floatingEmojis = useMemo(() => {
    if (playfulness <= 60) return [];
    const adjustedPlayfulness = playfulness - 60; // 0-40 range
    const count = Math.floor(adjustedPlayfulness / 8); // 0-5 emojis
    return Array.from({ length: count }, (_, i) => ({
      emoji: PLAYFUL_EMOJIS[i % PLAYFUL_EMOJIS.length],
      style: {
        '--float-delay': `${i * 0.3}s`,
        '--float-duration': `${2 + (i % 3)}s`,
        '--float-x': `${10 + (i * 15) % 80}%`,
      }
    }));
  }, [playfulness]);

  // Generate sparkles for experimental mode
  const sparkles = useMemo(() => {
    const count = Math.floor(experimentalness / 12); // 0-8 sparkles
    return Array.from({ length: count }, (_, i) => ({
      style: {
        '--sparkle-delay': `${i * 0.2}s`,
        '--sparkle-x': `${5 + (i * 12) % 90}%`,
        '--sparkle-y': `${10 + (i * 17) % 80}%`,
      }
    }));
  }, [experimentalness]);

  const hasKeywords = brandCore.vibeKeywords.some(k => k && k.trim());

  return (
    <div 
      className={`live-preview ${playfulness > 60 ? 'live-preview--playful' : ''} ${experimentalness > 50 ? 'live-preview--experimental' : ''}`}
      style={{
        ...styles,
        '--playfulness': Math.max(0, (playfulness - 50) / 50), // Only kicks in after 50
        '--experimentalness': experimentalness / 100,
      }}
    >
      <div className="live-preview__card">
        {/* Glitter overlay for experimental mode */}
        {experimentalness > 30 && (
          <div className="live-preview__glitter">
            {sparkles.map((sparkle, i) => (
              <span key={i} className="live-preview__sparkle" style={sparkle.style}>✦</span>
            ))}
          </div>
        )}
        
        {/* Floating emojis for playful mode - only when slider moved significantly */}
        {playfulness > 60 && floatingEmojis.length > 0 && (
          <div className="live-preview__emojis">
            {floatingEmojis.map((item, i) => (
              <span key={i} className="live-preview__floating-emoji" style={item.style}>
                {item.emoji}
              </span>
            ))}
          </div>
        )}

        {/* Animated border for experimental */}
        {experimentalness > 40 && <div className="live-preview__animated-border" />}

        <div className="live-preview__header">
          <div className="live-preview__logo">
            {brandCore.name ? brandCore.name.charAt(0).toUpperCase() : 'V'}
            {playfulness > 75 && <span className="live-preview__logo-bounce">✨</span>}
          </div>
          <div className="live-preview__brand">
            <h3 className="live-preview__name">
              {brandCore.name || 'Your Brand'}
              {playfulness > 85 && <span className="live-preview__wave">👋</span>}
            </h3>
            <span className="live-preview__tagline">
              {brandCore.mission 
                ? brandCore.mission.substring(0, 50) + (brandCore.mission.length > 50 ? '...' : '')
                : 'Your mission statement'}
            </span>
          </div>
        </div>

        {hasKeywords ? (
          <div className="live-preview__keywords">
            {brandCore.vibeKeywords.filter(k => k).map((keyword, i) => (
              <span key={i} className="live-preview__keyword">{keyword}</span>
            ))}
          </div>
        ) : (
          <div className="live-preview__keywords live-preview__keywords--empty">
            <span className="live-preview__keyword live-preview__keyword--placeholder">
              {playfulness > 70 ? '🎨 creative' : 'keyword'}
            </span>
          </div>
        )}

        <div className="live-preview__content">
          <div className="live-preview__bar" />
          <div className="live-preview__bar live-preview__bar--short" />
          <div className="live-preview__bar live-preview__bar--medium" />
        </div>

        <div className="live-preview__footer">
          <button className="live-preview__button">
            {playfulness > 75 ? "Let's Go! 🚀" : experimentalness > 60 ? '→ Explore' : 'Get Started'}
          </button>
          <span className="live-preview__link">
            {playfulness > 70 ? 'Discover more ✨' : 'Learn more →'}
          </span>
        </div>

        <div className="live-preview__accent-strip" />
        
        {/* Fun shapes for playful mode - only when slider moved past center */}
        {playfulness > 65 && (
          <div className="live-preview__shapes">
            <div className="live-preview__shape live-preview__shape--circle" />
            {playfulness > 75 && <div className="live-preview__shape live-preview__shape--triangle" />}
            {playfulness > 85 && <div className="live-preview__shape live-preview__shape--star" />}
          </div>
        )}
      </div>

      <div className="live-preview__specs">
        <div className="live-preview__spec">
          <span className="live-preview__spec-label">Color</span>
          <span 
            className="live-preview__spec-swatch"
            style={{ backgroundColor: visuals.colorSeed }}
          />
        </div>
        <div className="live-preview__spec">
          <span className="live-preview__spec-label">Mode</span>
          <span className="live-preview__spec-value">
            {visuals.colorMode === 'dark' ? '🌙' : '☀️'}
          </span>
        </div>
        <div className="live-preview__spec">
          <span className="live-preview__spec-label">Vibe</span>
          <span className="live-preview__spec-value">
            {playfulness > 80 ? '🎉' : playfulness > 60 ? '😊' : '💼'}
          </span>
        </div>
        <div className="live-preview__spec">
          <span className="live-preview__spec-label">Style</span>
          <span className="live-preview__spec-value">
            {experimentalness > 70 ? '🔥' : experimentalness > 40 ? '✨' : '📐'}
          </span>
        </div>
      </div>
    </div>
  );
}
